import React from 'react';
import { Link } from 'react-router-dom';
import { Dog, AlertTriangle, CheckCircle, Heart, Plane } from 'lucide-react';
import { SEOLayout, AlertBox } from '../../components/SEOLayout';
import { allBreedsData as breedsData } from '../../data/seoDataMerged';
import { Button } from '../../components/ui/button';

const BreedsHub = () => {
  const breeds = Object.values(breedsData);

  // Group by risk level
  const highRiskBreeds = breeds.filter(b => b.riskLevel === 'HIGH' || b.riskLevel === 'VERY HIGH');
  const moderateRiskBreeds = breeds.filter(b => b.riskLevel === 'MODERATE-HIGH');
  const lowRiskBreeds = breeds.filter(b => b.riskLevel === 'LOW' || b.riskLevel === 'LOW-MODERATE');

  const BreedCard = ({ breed }) => (
    <Link 
      to={`/breeds/${breed.slug}`}
      className="block bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-300 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{breed.name}</h3>
          <p className="text-slate-500 text-sm capitalize">{breed.type} • {breed.category}</p>
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${
          breed.riskLevel.includes('HIGH') ? 'bg-red-100 text-red-700' :
          breed.riskLevel.includes('MODERATE') ? 'bg-amber-100 text-amber-700' :
          'bg-green-100 text-green-700'
        }`}>
          {breed.riskLevel} Risk
        </div>
      </div>
      
      <div className="space-y-2 text-sm">
        {breed.airlineRestrictions?.bannedFromCargo?.length > 0 && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-4 h-4" />
            Banned from cargo on {breed.airlineRestrictions.bannedFromCargo.length}+ airlines
          </div>
        )}
        
        {breed.travelRisks?.respiratory && (
          <div className="text-slate-600">
            Respiratory risk: <span className="font-medium">{breed.travelRisks.respiratory}</span>
          </div>
        )}
      </div>
      
      <div className="mt-4 text-blue-600 text-sm font-medium">
        View travel guide →
      </div>
    </Link>
  );

  return (
    <SEOLayout
      title="Pet Breed Air Travel Guide 2026 | Airline Restrictions & Safety"
      description="Complete guide to flying with specific dog and cat breeds. Brachycephalic restrictions, airline bans, crate sizes, health risks. Know before you book."
      keywords={['dog breed airline restrictions', 'brachycephalic flying', 'pet breed air travel', 'snub nose dog airline', 'French Bulldog flying']}
      breadcrumbs={[{ label: 'Breeds' }]}
    >
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Heart className="w-4 h-4" />
          Know Your Breed's Risks
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Pet Breed Air Travel Guide
        </h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Not all breeds can fly safely. Snub-nosed dogs and cats face severe restrictions on many airlines. <span className="text-red-600 font-semibold">Some breeds are 
          BANNED from cargo holds entirely.</span> Find your breed's specific risks and requirements.
        </p>
      </div>

      {/* Critical Warning */}
      <div className="mb-12">
        <AlertBox type="danger" title="⚠️ Brachycephalic Breeds: Higher Respiratory Risk">
          <p className="mb-2">
            Snub-nosed (brachycephalic) breeds have shortened airways that make them vulnerable to respiratory distress, heat stroke, 
            and cardiac arrest - especially in the pressurized, temperature-variable cargo environment.
          </p>
          <p className="text-sm">
            French Bulldogs, English Bulldogs, Pugs, Boston Terriers, Boxers, Shih Tzus, and similar 
            breeds are banned from cargo on most major airlines. <strong>Cabin travel only</strong> - 
            if your pet fits under the seat.
          </p>
        </AlertBox>
      </div>


      {/* High Risk Breeds */}
      {highRiskBreeds.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            High Risk - Cargo Banned / Restricted
          </h2>
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <p className="text-red-800">
              These breeds face <strong>severe restrictions or complete bans</strong> from airline cargo holds. 
              <strong>Cabin travel only</strong> where size permits.
              Consider ground transport for long distances.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {highRiskBreeds.map(breed => (
              <BreedCard key={breed.slug} breed={breed} />
            ))}
          </div>
        </section>
      )}

      {/* Moderate Risk */}
      {moderateRiskBreeds.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Moderate Risk - Some Restrictions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {moderateRiskBreeds.map(breed => (
              <BreedCard key={breed.slug} breed={breed} />
            ))}
          </div>
        </section>
      )}

      {/* Low Risk */}
      {lowRiskBreeds.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            Low Risk - Generally Good Travelers
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lowRiskBreeds.map(breed => (
              <BreedCard key={breed.slug} breed={breed} />
            ))}
          </div>
        </section>
      )}

      {/* Brachycephalic Breeds List */}
      <section className="mt-16 bg-red-50 border border-red-200 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6" />
          Complete List: Brachycephalic Breeds Banned from Cargo
        </h2>
        <p className="text-red-800 mb-6">
          The following breeds are banned from cargo transport on most major airlines due to their 
          compromised respiratory systems:
        </p>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold text-red-900 mb-3">Dogs:</h3>
            <div className="flex flex-wrap gap-2">
              {[
                'Affenpinscher', 'American Bulldog', 'American Pit Bull', 'American Staffordshire Terrier',
                'Boston Terrier', 'Boxer', 'Brussels Griffon', 'Bulldog (English)', 'Bulldog (French)',
                'Bull Terrier', 'Cane Corso', 'Cavalier King Charles Spaniel', 'Chow Chow', 
                'Dogue de Bordeaux', 'English Toy Spaniel', 'Japanese Chin', 'King Charles Spaniel',
                'Lhasa Apso', 'Mastiff (all types)', 'Pekingese', 'Presa Canario', 'Pug',
                'Shar-Pei', 'Shih Tzu', 'Staffordshire Bull Terrier', 'Tibetan Spaniel'
              ].map((breed, index) => (
                <span key={index} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                  {breed}
                </span>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-red-900 mb-3">Cats:</h3>
            <div className="flex flex-wrap gap-2">
              {[
                'Persian', 'Himalayan', 'Exotic Shorthair', 'British Shorthair', 
                'Scottish Fold', 'Burmese'
              ].map((breed, index) => (
                <span key={index} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                  {breed}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="mt-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-pink-600 mb-8">
            Understanding Breed-Specific Air Travel Risks
          </h2>
          
          <div className="space-y-6">
            <p className="text-lg text-slate-700 leading-relaxed">
              The tragic reality of pet air travel is that some breeds simply cannot fly safely in cargo holds. 
              The pressurized, temperature-variable environment of an aircraft cargo compartment poses serious 
              risks to pets with compromised respiratory systems, thick coats, or anxiety-prone temperaments.
            </p>
            
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 border border-red-100">
              <h3 className="text-xl font-bold text-red-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Why Brachycephalic Breeds Are Banned
              </h3>
              <p className="text-red-700 leading-relaxed mb-4">
                Brachycephalic (flat-faced) breeds like French Bulldogs, Pugs, and English Bulldogs have 
                severely shortened airways due to selective breeding. Their elongated soft palates, 
                narrowed nostrils, and compressed tracheas make breathing difficult even in normal conditions. 
                In the cargo hold, these breeds are at extreme risk of:
              </p>
              <ul className="space-y-2 text-red-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Respiratory failure</strong> - Already labored breathing becomes critical</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Heat stroke</strong> - Cannot pant effectively to regulate temperature</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Cardiac arrest</strong> - Stress combined with breathing difficulty</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  <span><strong>Hypoxia</strong> - Oxygen deprivation at altitude</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <h3 className="text-xl font-bold text-green-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Safe Alternatives for At-Risk Breeds
              </h3>
              <p className="text-green-700 leading-relaxed mb-4">
                If you have a brachycephalic breed and need to travel long distance, consider these alternatives:
              </p>
              <ul className="space-y-2 text-green-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span><strong>Cabin travel</strong> - If your pet fits in an under-seat carrier (usually under 8kg)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span><strong>Ground transport services</strong> - Companies specialize in driving pets cross-country</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span><strong>Pet relocation specialists</strong> - Can arrange safest possible travel</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span><strong>Driving yourself</strong> - Climate-controlled car is safest option</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </SEOLayout>
  );
};

export default BreedsHub;
